import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { BehaviorSubject, debounceTime, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Optional } from '@src/types/utils';
import { ActionItemModel } from '@src/app/modules/actions';
import { AlertService } from '@src/core/services';
import { SessionService } from '@src/app/modules/auth';
import { BaseComponent } from '@src/app/modules/base';

import { MessageContentTypeEnum, MessageModel } from '../types';
import { MessageService } from '../services';
import { DialogService, StoreService } from '../../services';
import { ChatPermissionsModel } from '../../chat';
import { messageGroupData } from '../utils';
import { htmlToText } from '../../utils';

@Component({
  selector: 'messages-list',
  templateUrl: './messages-list.component.html',
  styleUrls: ['./messages-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MessagesListComponent extends BaseComponent implements OnInit, OnChanges {
  private readonly messageService = inject(MessageService);
  private readonly store = inject(StoreService);
  private readonly dialogService = inject(DialogService);
  private readonly clipboard = inject(Clipboard);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly session = inject(SessionService);

  @Input() chatId!: string;
  @Input() messages: Optional<Array<MessageModel> | null>;
  @Input() lastReadMessageId?: Optional<string | null>;
  @Input() firstIncomingUnreadMessageId?: Optional<string | null>;
  @Input() permissions?: ChatPermissionsModel | null;
  @Input() allMessagesOnScreen?: boolean; // TODO: Убрать после исправления waIntersectionObserver. Если скролла нет, то observer не всегда эмитит правильное значение

  groupData: Record<string, string> = {};

  readonly lastReadMessage$ = new BehaviorSubject<MessageModel | undefined>(undefined);
  readonly MessageContentTypeEnum = MessageContentTypeEnum;

  readonly contextMenuActions: ActionItemModel[] = [
    {
      title: this.translateService.instant('components.messageList.buttons.reply'),
      icon: 'reply',
      visible: () => {
        return !!this.permissions?.canSendMessages;
      },
      action: (message: MessageModel) => {
        this.store.setFooterMessageState(this.chatId, {
          mode: 'reply',
          message,
        });
        this.cdr.markForCheck();
      },
    },
    {
      title: this.translateService.instant('components.messageList.buttons.copy'),
      icon: 'copy',
      visible: true,
      action: (message: MessageModel) => {
        const copyText = htmlToText(message.content.text);

        if (copyText && this.clipboard.copy(copyText)) {
          this.alertService.success(
            this.translateService.instant('components.messageList.alerts.successes.copyMessageText'),
          );
        }
      },
    },
    {
      title: this.translateService.instant('components.messageList.buttons.edit'),
      icon: 'edit-2',
      visible: (message: MessageModel) => {
        return message.userId === this.session.authUser?.id;
      },
      action: (message: MessageModel) => {
        this.store.setFooterMessageState(this.chatId, {
          mode: 'edit',
          message,
        });
        this.store.setDraftMessageState(this.chatId, message.content.text ?? '');
        this.cdr.markForCheck();
      },
    },
    {
      title: this.translateService.instant('components.messageList.buttons.delete'),
      icon: '@tui.trash-2',
      color: 'var(--int-mes-color-red)',
      visible: (message: MessageModel) => {
        return message.userId === this.session.authUser?.id || !!this.permissions?.canDeleteMessages;
      },
      action: (message: MessageModel) => {
        this.openConfirmDeleteMessageDialog(this.chatId, message);
      },
    },
  ];

  ngOnInit(): void {
    this.lastReadMessage$.pipe(debounceTime(500), takeUntil(this.destroyed$$)).subscribe(message => {
      if (message) {
        this.messageService.readMessage(message.chatId, message.id);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messages'] && this.messages) {
      this.groupData = messageGroupData(this.messages, this.translateService.currentLang);
    }
  }

  private openConfirmDeleteMessageDialog(chatId: string, message: MessageModel): void {
    this.dialogService.confirmDeleteMessageDialog.subscribe(res => {
      if (res) {
        if (!message.id) {
          // TODO: вывести ошибку
          return;
        }

        this.messageService.deleteMessage(chatId, message.id).subscribe({
          complete: () => {
            // TODO: логику можно перенести на бэк, потому что для группы файлов бэк уже удаляет файлы самостоятельно
            if (message.contentType === 'file') {
              message.content.files?.forEach(file => {
                const fileId = file.fileId;
                if (!fileId) {
                  // TODO: вывести ошибку
                  return;
                }

                this.messageService.deleteChatFile(chatId, fileId).subscribe();
              });
            }
          },
        });
      }
    });
  }

  sendReaction(emodjiName: string, message: MessageModel) {
    if (!emodjiName) {
      return;
    }

    this.messageService.sendReaction(this.chatId, message.id, emodjiName).subscribe();
  }

  onIntersection(
    [entry]: IntersectionObserverEntry[],
    intersectionMessage: MessageModel,
    intObserverReading: IntersectionObserver,
  ) {
    const debouncedLastReadMessageId = this.lastReadMessage$.value?.id;

    if (
      !this.messages ||
      (this.lastReadMessageId && intersectionMessage.id <= this.lastReadMessageId) ||
      (debouncedLastReadMessageId && intersectionMessage.id <= debouncedLastReadMessageId)
    ) {
      intObserverReading.unobserve(entry.target);
      return;
    }

    if (entry.isIntersecting || this.allMessagesOnScreen) {
      this.lastReadMessage$.next(intersectionMessage);

      intObserverReading.unobserve(entry.target);
    }
  }
}
